<ng-template #fieldGrid let-fields>
  <dl class="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
    @for (field of fields; track field[0]) {
      <div class="min-w-0">
        <dt class="text-[11px] uppercase text-gray-400">{{ formatFieldLabel(field[0]) }}</dt>
        <dd class="wrap-break-word text-sm text-gray-800 dark:text-gray-200">{{ formatValue(field[1]) }}</dd>
      </div>
    }
  </dl>
</ng-template>

<!-- Above the app header (z-99999), which would otherwise cover the top of a tall dialog. -->
<div class="fixed inset-0 z-999999 flex items-center justify-center bg-black/40 p-4" (click)="close()">
  <div class="flex max-h-[90vh] w-full max-w-5xl flex-col rounded-2xl bg-white shadow-xl dark:bg-gray-900" (click)="$event.stopPropagation()">

    <!-- Header -->
    <div class="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4 dark:border-gray-800">
      <div>
        <h3 class="text-base font-semibold text-gray-800 dark:text-white">Customer Details</h3>
        <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
          @if (customerName) { <span class="font-medium text-gray-700 dark:text-gray-300">{{ customerName }}</span> &middot; }
          Survey Request {{ surveyRequestId }}
        </p>
      </div>
      <button type="button" (click)="close()" class="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-800 dark:hover:text-gray-200" aria-label="Close">
        <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
      </button>
    </div>

    @if (loading()) {
      <div class="flex justify-center py-16">
        <svg class="h-8 w-8 animate-spin text-blue-500" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/></svg>
      </div>
    } @else if (error()) {
      <p class="m-6 rounded-lg border border-red-200 bg-red-100 p-3 text-sm text-red-600">{{ error() }}</p>
    } @else if (details(); as d) {
      <!-- Tabs -->
      <div class="flex gap-1 border-b border-gray-200 px-6 dark:border-gray-800">
        <button type="button" (click)="activeTab.set('customer')"
          class="-mb-px border-b-2 px-3 py-2 text-sm font-medium"
          [class.border-blue-600]="activeTab() === 'customer'" [class.text-blue-600]="activeTab() === 'customer'"
          [class.border-transparent]="activeTab() !== 'customer'" [class.text-gray-500]="activeTab() !== 'customer'">
          Customer
        </button>
        <button type="button" (click)="activeTab.set('email')"
          class="-mb-px border-b-2 px-3 py-2 text-sm font-medium"
          [class.border-blue-600]="activeTab() === 'email'" [class.text-blue-600]="activeTab() === 'email'"
          [class.border-transparent]="activeTab() !== 'email'" [class.text-gray-500]="activeTab() !== 'email'">
          Email Notifications <span class="ml-1 text-xs text-gray-400">({{ d.emailNotifications.length }})</span>
        </button>
        <button type="button" (click)="activeTab.set('mobile')"
          class="-mb-px border-b-2 px-3 py-2 text-sm font-medium"
          [class.border-blue-600]="activeTab() === 'mobile'" [class.text-blue-600]="activeTab() === 'mobile'"
          [class.border-transparent]="activeTab() !== 'mobile'" [class.text-gray-500]="activeTab() !== 'mobile'">
          Mobile Notifications <span class="ml-1 text-xs text-gray-400">({{ d.mobileNotifications.length }})</span>
        </button>
        <button type="button" (click)="activeTab.set('response')"
          class="-mb-px border-b-2 px-3 py-2 text-sm font-medium"
          [class.border-blue-600]="activeTab() === 'response'" [class.text-blue-600]="activeTab() === 'response'"
          [class.border-transparent]="activeTab() !== 'response'" [class.text-gray-500]="activeTab() !== 'response'">
          Response
        </button>
      </div>

      <!-- Body -->
      <div class="overflow-y-auto px-6 py-5">
        @switch (activeTab()) {
          @case ('customer') {
            <ng-container *ngTemplateOutlet="fieldGrid; context: { $implicit: fieldsOf(d.customerData) }"/>
          }

          @case ('email') {
            @for (notification of d.emailNotifications; track $index) {
              <div class="mb-4 rounded-lg border border-gray-200 p-4 last:mb-0 dark:border-gray-800">
                <div class="mb-3 flex items-center justify-between gap-3">
                  <div class="min-w-0">
                    <span class="block text-[11px] uppercase text-gray-400">Subject</span>
                    <span class="text-sm font-semibold text-gray-700 dark:text-gray-200">{{ formatValue(notification.subject) }}</span>
                  </div>
                  <span class="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">{{ notification.status }}</span>
                </div>
                <ng-container *ngTemplateOutlet="fieldGrid; context: { $implicit: fieldsOf(notification) }"/>
              </div>
            } @empty {
              <p class="py-8 text-center text-sm text-gray-400">No email notifications.</p>
            }
          }

          @case ('mobile') {
            @for (notification of d.mobileNotifications; track $index) {
              <div class="mb-4 rounded-lg border border-gray-200 p-4 last:mb-0 dark:border-gray-800">
                <div class="mb-3 flex items-center justify-between gap-3">
                  <div class="min-w-0">
                    <span class="block text-[11px] uppercase text-gray-400">Alpha Name</span>
                    <span class="text-sm font-semibold text-gray-700 dark:text-gray-200">{{ formatValue(notification.alphaName) }}</span>
                  </div>
                  <span class="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 dark:bg-gray-800 dark:text-gray-300">{{ notification.status }}</span>
                </div>
                <ng-container *ngTemplateOutlet="fieldGrid; context: { $implicit: fieldsOf(notification) }"/>
              </div>
            } @empty {
              <p class="py-8 text-center text-sm text-gray-400">No mobile notifications.</p>
            }
          }

          @case ('response') {
            @if (d.response; as response) {
              <ng-container *ngTemplateOutlet="fieldGrid; context: { $implicit: fieldsOf(response, ['answers', 'callDetails']) }"/>

              <h4 class="mb-2 mt-6 text-sm font-semibold text-gray-700 dark:text-gray-200">Answers <span class="text-xs font-normal text-gray-400">({{ response.answers.length }})</span></h4>
              <div class="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table class="w-full text-left text-xs text-gray-700 dark:text-gray-300">
                  <thead class="bg-gray-50 uppercase text-gray-500 dark:bg-gray-800 dark:text-gray-400">
                    <tr>
                      <th class="px-3 py-2">Question Code</th>
                      <th class="px-3 py-2">Question Type</th>
                      <th class="px-3 py-2">Text Value</th>
                      <th class="px-3 py-2">Numeric Value</th>
                      <th class="px-3 py-2">Choice Value</th>
                      <th class="px-3 py-2">Field Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    @for (answer of response.answers; track $index) {
                      <tr class="border-t border-gray-100 dark:border-gray-800">
                        <td class="px-3 py-2 font-mono">{{ answer.questionCode }}</td>
                        <td class="px-3 py-2">{{ formatValue(answer.questionType) }}</td>
                        <td class="px-3 py-2">{{ formatValue(answer.textValue) }}</td>
                        <td class="px-3 py-2">{{ formatValue(answer.numericValue) }}</td>
                        <td class="px-3 py-2">{{ formatValue(answer.choiceValue) }}</td>
                        <td class="px-3 py-2">{{ formatValue(answer.fieldName) }}</td>
                      </tr>
                    } @empty {
                      <tr><td colspan="6" class="px-3 py-6 text-center text-gray-400">No answers.</td></tr>
                    }
                  </tbody>
                </table>
              </div>

              @if (response.callDetails; as call) {
                <h4 class="mb-2 mt-6 text-sm font-semibold text-gray-700 dark:text-gray-200">Call Details</h4>
                <div class="rounded-lg border border-gray-200 p-4 dark:border-gray-800">
                  <ng-container *ngTemplateOutlet="fieldGrid; context: { $implicit: fieldsOf(call) }"/>
                </div>
              }
            } @else {
              <p class="py-8 text-center text-sm text-gray-400">No response recorded.</p>
            }
          }
        }
      </div>
    }
  </div>
</div>
